import { useState, useEffect, useCallback } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ChevronDown,
  Compass,
  Gamepad2,
  Globe,
  Loader2,
  Lock,
  Plus,
  Shield,
  SortDesc,
  Sparkles,
  Swords,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

import { Avatar, Badge, Button, SearchInput } from '../../../components/ui';
import { trpc } from '../../../lib/trpc';

export const Route = createFileRoute('/app/clubs/')({
  staticData: { title: 'Discover Clubs' },
  component: ClubDiscoveryPage,
});

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ClubDiscoveryPage() {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [allItems, setAllItems] = useState<ClubItem[]>([]);
  const [joinedIds, setJoinedIds] = useState<Set<string>>(new Set());
  const [gameSlug, setGameSlug] = useState<string | undefined>();
  const [sort, setSort] = useState<'newest' | 'most_members'>('newest');

  const debouncedSearch = useDebounce(search, 300);

  const { data: gamesList } = trpc.game.list.useQuery();

  const { data, isLoading, isFetching } = trpc.club.list.useQuery({
    search: debouncedSearch || undefined,
    cursor,
    limit: 12,
    gameSlug,
    sort,
  });

  const utils = trpc.useUtils();

  const joinMutation = trpc.clubMember.join.useMutation({
    onSuccess: (_data, variables) => {
      const clubId = 'clubId' in variables ? variables.clubId : '';
      setJoinedIds((prev) => new Set(prev).add(clubId));
      utils.club.getMyClubs.invalidate();
      toast.success('Joined club!');
    },
    onError: (error) => {
      if (error.data?.code === 'CONFLICT') {
        toast.error('You are already a member of this club');
      } else if (error.data?.code === 'FORBIDDEN') {
        toast.error('This club is not accepting new members');
      } else {
        toast.error('Failed to join club. Please try again.');
      }
    },
  });

  // Reset accumulated items when search/filters change
  useEffect(() => {
    setCursor(undefined);
    setAllItems([]);
  }, [debouncedSearch, gameSlug, sort]);

  // Accumulate items as pages load
  useEffect(() => {
    if (data?.items) {
      if (!cursor) {
        setAllItems(data.items);
      } else {
        setAllItems((prev) => [...prev, ...data.items]);
      }
    }
  }, [data, cursor]);

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
    }
  }, [data?.nextCursor]);

  const handleJoin = useCallback(
    (clubId: string) => {
      joinMutation.mutate({ clubId });
    },
    [joinMutation],
  );

  const items = allItems;
  const hasMore = !!data?.nextCursor;
  const showSkeleton = isLoading && items.length === 0;

  return (
    <div className="relative max-w-6xl mx-auto p-4 md:p-6">
      {/* ── Hero section ─────────────────────────────────────────────── */}
      <div className="relative mb-10 overflow-hidden rounded-2xl">
        {/* Layered background */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-900/60 via-surface-900/90 to-surface-950" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(38,217,181,0.15)_0%,_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(38,217,181,0.08)_0%,_transparent_50%)]" />
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Noise */}
        <div className="absolute inset-0 noise-overlay" />

        {/* Content */}
        <div className="relative px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Icon cluster */}
          <div className="relative shrink-0">
            <div className="absolute -inset-3 rounded-full bg-brand-500/20 blur-2xl animate-pulse-glow" />
            <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-500/15 border border-brand-500/30 backdrop-blur-sm">
              <Compass className="size-8 text-brand-400" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">
              Discover Clubs
            </h1>
            <p className="text-surface-400 mt-1 text-sm sm:text-base">
              Find your arena. Join a competitive community and climb the ranks.
            </p>
          </div>

          {/* Stats strip */}
          <div className="flex items-center gap-5 text-center shrink-0">
            <div>
              <p className="text-2xl font-display font-bold text-brand-400">
                {items.length > 0 ? items.length : '--'}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-surface-500 font-semibold">
                Clubs
              </p>
            </div>
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-surface-600 to-transparent" />
            <div>
              <p className="text-2xl font-display font-bold">
                {items.length > 0
                  ? items.reduce((sum, c) => sum + c.memberCount, 0)
                  : '--'}
              </p>
              <p className="text-[10px] uppercase tracking-widest text-surface-500 font-semibold">
                Players
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Search + Filters ──────────────────────────────────────────── */}
      <div className="mb-8 flex flex-col sm:flex-row gap-3">
        <SearchInput
          placeholder="Search clubs by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          loading={isFetching && !!search}
          className="max-w-md flex-1"
        />

        <div className="flex items-center gap-2">
          {/* Game filter */}
          <div className="relative">
            <select
              value={gameSlug ?? ''}
              onChange={(e) => setGameSlug(e.target.value || undefined)}
              className="appearance-none h-10 pl-9 pr-8 rounded-lg bg-surface-800 border border-surface-700 text-sm text-surface-200 hover:border-surface-600 focus:border-brand-500 focus:outline-none transition-colors cursor-pointer"
            >
              <option value="">All Games</option>
              {gamesList?.map((g) => (
                <option key={g.slug} value={g.slug}>{g.name}</option>
              ))}
            </select>
            <Gamepad2 className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-surface-400 pointer-events-none" />
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-3.5 text-surface-500 pointer-events-none" />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as 'newest' | 'most_members')}
              className="appearance-none h-10 pl-9 pr-8 rounded-lg bg-surface-800 border border-surface-700 text-sm text-surface-200 hover:border-surface-600 focus:border-brand-500 focus:outline-none transition-colors cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="most_members">Most Members</option>
            </select>
            <SortDesc className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-surface-400 pointer-events-none" />
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-3.5 text-surface-500 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* ── Grid ─────────────────────────────────────────────────────── */}
      {showSkeleton ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <ClubCardSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState search={debouncedSearch} />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((club) => (
              <ClubCard
                key={club.id}
                club={club}
                joined={joinedIds.has(club.id)}
                joining={
                  joinMutation.isPending &&
                  joinMutation.variables != null &&
                  'clubId' in joinMutation.variables &&
                  joinMutation.variables.clubId === club.id
                }
                onJoin={handleJoin}
              />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-8 pb-2">
              <Button
                variant="outline"
                size="lg"
                onClick={handleLoadMore}
                disabled={isFetching}
                leftIcon={
                  isFetching ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )
                }
              >
                {isFetching ? 'Loading...' : 'Load More Clubs'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ClubItem = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  avatarUrl: string | null;
  bannerUrl: string | null;
  privacy: 'public' | 'private' | 'invite_only';
  memberCount: number;
  onlineMemberCount: number;
  games: { name: string; slug: string }[];
};

// ---------------------------------------------------------------------------
// Privacy config
// ---------------------------------------------------------------------------

const privacyConfig = {
  public: {
    icon: Globe,
    label: 'Public',
    color: 'green' as const,
    glow: 'from-green-500/20 to-green-600/5',
    dot: 'bg-green-400',
  },
  private: {
    icon: Lock,
    label: 'Private',
    color: 'yellow' as const,
    glow: 'from-yellow-500/15 to-yellow-600/5',
    dot: 'bg-yellow-400',
  },
  invite_only: {
    icon: Shield,
    label: 'Invite Only',
    color: 'purple' as const,
    glow: 'from-purple-500/15 to-purple-600/5',
    dot: 'bg-purple-400',
  },
};

// ---------------------------------------------------------------------------
// Club Card
// ---------------------------------------------------------------------------

function ClubCard({
  club,
  joined,
  joining,
  onJoin,
}: {
  club: ClubItem;
  joined: boolean;
  joining: boolean;
  onJoin: (id: string) => void;
}) {
  const privacy = privacyConfig[club.privacy];
  const PrivacyIcon = privacy.icon;
  const isPublic = club.privacy === 'public';

  return (
    <Link
      to="/app/clubs/$slug"
      params={{ slug: club.slug }}
      className="group relative flex flex-col rounded-2xl bg-surface-800/50 border border-surface-700/50 hover:border-brand-500/30 transition-all duration-300 overflow-hidden cursor-pointer"
    >
      {/* ── Banner ─────────────────────────────────────────────────── */}
      <div className="relative h-28 overflow-hidden bg-surface-700/50 shrink-0">
        {club.bannerUrl ? (
          <img
            src={club.bannerUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          /* Gradient placeholder when no banner */
          (<div className={`w-full h-full bg-gradient-to-br ${privacy.glow} opacity-60`} />)
        )}
        {/* Dark gradient overlay at bottom for contrast with avatar */}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface-800/90 to-transparent" />

        {/* Privacy badge on banner */}
        <div className="absolute top-2.5 right-2.5">
          <Badge variant="subtle" color={privacy.color} size="sm" className="backdrop-blur-md bg-surface-900/80">
            <PrivacyIcon className="size-3 mr-1" />
            {privacy.label}
          </Badge>
        </div>
      </div>
      {/* ── Avatar overlapping banner edge ──────────────────────── */}
      <div className="relative px-4 -mt-6">
        <div className="relative inline-block">
          <Avatar
            size="lg"
            variant="rounded"
            src={club.avatarUrl}
            initials={club.name.charAt(0)}
            alt={club.name}
            className="ring-[3px] ring-surface-800 group-hover:ring-brand-500/40 transition-all duration-300"
          />
        </div>
      </div>
      {/* ── Card body ──────────────────────────────────────────────── */}
      <div className="relative flex flex-col flex-1 px-4 pb-4 pt-2 gap-2.5">
        {/* Name */}
        <h3 className="font-display font-bold text-white text-lg leading-tight truncate group-hover:text-brand-400 transition-colors duration-200">
          {club.name}
        </h3>

        {/* Description */}
        {club.description && (
          <p className="text-sm text-surface-400 line-clamp-2 leading-relaxed">
            {club.description}
          </p>
        )}

        {/* Game tags */}
        {club.games.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {club.games.map((game) => (
              <span
                key={game.slug}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-surface-700/50 border border-surface-600/30 text-xs font-medium text-surface-300 group-hover:border-surface-600/60 transition-colors"
              >
                <Gamepad2 className="size-3 text-brand-500/60" />
                {game.name}
              </span>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Stats + action row */}
        <div className="flex items-center justify-between pt-2 border-t border-surface-700/40">
          {/* Member + online count */}
          <div className="flex items-center gap-3 text-xs text-surface-400">
            <div className="flex items-center gap-1.5">
              <Users className="size-3.5 text-surface-500" />
              <span className="font-semibold tabular-nums">
                {club.memberCount.toLocaleString()}
              </span>
            </div>
            {club.onlineMemberCount > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="font-semibold tabular-nums text-green-400">
                  {club.onlineMemberCount}
                </span>
                <span className="text-surface-500">online</span>
              </div>
            )}
          </div>

          {/* Join button */}
          {joined ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-400">
              <Sparkles className="size-3.5" />
              Joined
            </span>
          ) : isPublic ? (
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => { e.preventDefault(); onJoin(club.id); }}
              disabled={joining}
              leftIcon={joining ? <Loader2 className="size-3.5 animate-spin" /> : undefined}
            >
              {joining ? 'Joining' : 'Join'}
            </Button>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-surface-500">
              <Lock className="size-3" />
              {club.privacy === 'invite_only' ? 'Invite Only' : 'Private'}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ClubCardSkeleton() {
  return (
    <div className="relative flex flex-col rounded-2xl bg-surface-800/50 border border-surface-700/50 overflow-hidden">
      {/* Banner skeleton */}
      <div className="h-28 bg-surface-700/40 animate-shimmer" style={{ backgroundSize: "200% 100%", backgroundImage: "linear-gradient(90deg, transparent 0%, rgba(38,217,181,0.03) 50%, transparent 100%)" }} />
      {/* Avatar skeleton overlapping */}
      <div className="px-4 -mt-6">
        <div className="w-12 h-12 rounded-xl bg-surface-700 ring-[3px] ring-surface-800" />
      </div>
      {/* Body */}
      <div className="px-4 pb-4 pt-2 space-y-3">
        <div className="h-5 w-2/3 rounded-lg bg-surface-700/60" />
        <div className="space-y-1.5">
          <div className="h-3 w-full rounded bg-surface-700/40" />
          <div className="h-3 w-4/5 rounded bg-surface-700/30" />
        </div>
        <div className="flex gap-1.5">
          <div className="h-7 w-24 rounded-lg bg-surface-700/30" />
        </div>
        <div className="h-px bg-surface-700/30" />
        <div className="flex items-center justify-between">
          <div className="h-3 w-24 rounded bg-surface-700/30" />
          <div className="h-8 w-16 rounded-lg bg-surface-700/40" />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function EmptyState({ search }: { search: string }) {
  return (
    <div className="relative flex flex-col items-center justify-center py-24 text-center">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(38,217,181,0.06)_0%,_transparent_70%)]" />

      <div className="relative">
        {/* Icon with glow */}
        <div className="relative mx-auto mb-6">
          <div className="absolute inset-0 rounded-full bg-brand-500/15 blur-2xl scale-150" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-surface-800/80 border border-surface-700/50">
            {search ? (
              <Compass className="size-10 text-surface-500" />
            ) : (
              <Swords className="size-10 text-surface-500" />
            )}
          </div>
        </div>

        <h3 className="text-xl font-display font-bold text-white mb-2">
          {search ? 'No clubs found' : 'No clubs yet'}
        </h3>
        <p className="text-sm text-surface-400 max-w-sm mb-6">
          {search
            ? `Nothing matches "${search}". Try a different search or browse all clubs.`
            : 'The arena awaits its first contenders. Create a club and rally your squad.'}
        </p>

        {!search && (
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus className="size-4" />}
          >
            Create a Club
          </Button>
        )}
      </div>
    </div>
  );
}
